/**
 * MODULE: domain/pages/frontend/components/editor/PagesTranscriptPreviewBlockView.tsx
 * PURPOSE: Live call transcript preview card block
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'flowbite-react';
import { HiOutlineMicrophone, HiOutlineRefresh } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import { useCallTranscriptPreview } from '../../hooks/useCallTranscriptPreview';
import {
  formatCallDuration,
  formatCallOccurredAt,
  sentimentBadgeClass,
  sentimentLabel,
} from '../../utils/pageCallPreviewUtils';

export function PagesTranscriptPreviewBlockView({ node, selected }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const callId = String(node.attrs.callId ?? '');
  const fallbackLabel = String(node.attrs.callLabel ?? 'Recorded call').trim() || 'Recorded call';
  const [showFullTranscript, setShowFullTranscript] = useState(false);

  const { data, isLoading, isError, refetch, isFetching } = useCallTranscriptPreview(callId || null, {
    enabled: !isPublicView,
  });

  const title = isPublicView ? fallbackLabel : (data?.label ?? fallbackLabel);
  const occurredAt = !isPublicView ? formatCallOccurredAt(data?.occurredAt) : null;
  const duration = !isPublicView ? formatCallDuration(data?.durationSeconds) : null;
  const direction = !isPublicView && data?.direction
    ? data.direction.replace(/_/g, ' ')
    : null;
  const metaParts = isPublicView
    ? ['Saved call reference from the shared page']
    : [occurredAt, duration, direction].filter(Boolean);
  const metaLine = metaParts.join(' · ') || 'Open Calls for full recording and analysis';

  const summary = !isPublicView ? data?.summary : null;
  const transcript = !isPublicView
    ? showFullTranscript
      ? data?.fullTranscript
      : data?.transcriptExcerpt
    : null;
  const sentiment = !isPublicView ? sentimentLabel(data?.sentimentScore) : null;
  const topics = !isPublicView ? (data?.topics ?? []).slice(0, 4) : [];
  const actionItemCount = !isPublicView ? (data?.actionItemCount ?? 0) : 0;
  const transcriptPending =
    !isPublicView &&
    data?.transcriptStatus &&
    data.transcriptStatus !== 'completed' &&
    !data.fullTranscript;

  return (
    <NodeViewWrapper className="my-3">
      <div
        className={`overflow-hidden rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white shadow-sm dark:border-violet-900/50 dark:from-violet-950/30 dark:to-gray-900 ${
          selected ? 'ring-2 ring-violet-400 ring-offset-2 dark:ring-violet-600 dark:ring-offset-gray-950' : ''
        }`}
        data-testid="pages-transcript-preview-block"
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-200">
            <HiOutlineMicrophone className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p
                className="truncate text-base font-semibold text-gray-900 dark:text-white"
                data-testid="pages-transcript-preview-title"
              >
                {title}
              </p>
              <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-violet-800 dark:bg-violet-900/40 dark:text-violet-200">
                Call transcript
              </span>
              {sentiment ? (
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${sentimentBadgeClass(data?.sentimentScore)}`}
                  data-testid="pages-transcript-preview-sentiment"
                >
                  {sentiment}
                </span>
              ) : null}
            </div>
            <p
              className="mt-1 text-xs text-gray-600 dark:text-gray-400"
              data-testid="pages-transcript-preview-meta"
            >
              {metaLine}
            </p>

            {isPublicView ? (
              <p
                className="mt-3 text-sm text-gray-500 dark:text-gray-400"
                data-testid="pages-transcript-preview-public-note"
              >
                Live transcript and AI summary are available when you open this page in the workspace.
              </p>
            ) : isLoading ? (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                <Spinner size="sm" aria-label="Loading call transcript" />
                Loading transcript preview…
              </div>
            ) : isError ? (
              <p
                className="mt-3 text-sm text-red-600 dark:text-red-400"
                data-testid="pages-transcript-preview-error"
              >
                Could not load call data. Showing saved label — try refresh.
              </p>
            ) : (
              <div className="mt-3 space-y-3">
                {summary ? (
                  <div data-testid="pages-transcript-preview-summary">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Summary
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                      {summary}
                    </p>
                  </div>
                ) : null}

                {transcriptPending ? (
                  <p
                    className="text-sm text-amber-700 dark:text-amber-300"
                    data-testid="pages-transcript-preview-pending"
                  >
                    Transcript is still processing…
                  </p>
                ) : transcript ? (
                  <div data-testid="pages-transcript-preview-excerpt">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Transcript
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                      {transcript}
                    </p>
                    {data?.fullTranscript &&
                    data.fullTranscript.length > (data.transcriptExcerpt?.length ?? 0) ? (
                      <button
                        type="button"
                        className="mt-2 text-xs font-medium text-violet-700 hover:underline dark:text-violet-300"
                        data-testid="pages-transcript-preview-toggle"
                        onClick={() => setShowFullTranscript((value) => !value)}
                      >
                        {showFullTranscript ? 'Show excerpt' : 'Show full transcript'}
                      </button>
                    ) : null}
                  </div>
                ) : !summary ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No transcript available for this call yet.
                  </p>
                ) : null}

                {topics.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="pages-transcript-preview-topics">
                    {topics.map((topic) => (
                      <span
                        key={topic}
                        className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] text-gray-700 ring-1 ring-violet-100 dark:bg-gray-900/60 dark:text-gray-300 dark:ring-violet-900/50"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                ) : null}

                {actionItemCount > 0 ? (
                  <p
                    className="text-xs text-gray-500 dark:text-gray-400"
                    data-testid="pages-transcript-preview-action-items"
                  >
                    {actionItemCount} action item{actionItemCount === 1 ? '' : 's'} from analysis
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {!isPublicView && callId ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-violet-100 px-4 py-2 dark:border-violet-900/40">
            <Button
              size="xs"
              color="light"
              onClick={() => void refetch()}
              disabled={isFetching}
              data-testid="pages-transcript-preview-refresh"
            >
              <HiOutlineRefresh className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
            </Button>
            <Button
              size="xs"
              color="light"
              onClick={() => navigate('/calls')}
              data-testid="pages-transcript-preview-open-calls"
            >
              Open Calls
            </Button>
          </div>
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}
